// Admin list page for triage BoardColumns. Per-org. List rows are
// drag-and-drop reorderable via the HTML5 native DnD API (no new
// dependency — the list is small and bounded by an org's outcome set,
// so a heavyweight DnD library would be overkill). The drag handle on
// the left of each row is the primary affordance; the row body itself
// is the drop target. Drop persists the new order via the existing
// `PATCH /v1/board-columns/reorder` from DEV-16183. (DEV-16192.)
import { useClient } from '@cortex/react-query';
import type { BoardColumn, IPaginated } from '@cortex/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type CSSProperties, type DragEvent } from 'react';

import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { boardColumnAccentToken } from '../../lib/board-column-colors';
import { routes } from '../../lib/constants';
import { formatRelative } from '../../lib/format';

export const Route = createFileRoute('/board-columns/')({ component: BoardColumnsPage });

function BoardColumnsPage() {
  const client = useClient();
  const qc = useQueryClient();
  const nav = useNavigate();

  const list = useQuery({
    queryKey: ['board-columns'],
    queryFn: () => client.http.get('v1/board-columns?limit=500').json<IPaginated<BoardColumn>>(),
  });

  // Optimistic local order shown during a drag — flushed back to the
  // server query once the reorder mutation succeeds. Lets the user see
  // the row "snap" into its new position immediately instead of after
  // the network round-trip.
  const [localOrder, setLocalOrder] = useState<string[] | null>(null);
  useEffect(() => {
    // Reset whenever the server-side list changes (initial load,
    // external edit). The local override only matters while a drop is
    // in-flight; once the server confirms, fall back to its truth.
    setLocalOrder(null);
  }, [list.data]);

  const reorder = useMutation({
    mutationFn: (ids: string[]) =>
      client.http.patch('v1/board-columns/reorder', { json: { ids } }).json<BoardColumn[]>(),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['board-columns'] });
      setLocalOrder(null);
    },
    onError: () => {
      // Roll the optimistic order back so the user sees the real state.
      setLocalOrder(null);
    },
  });

  const serverItems = list.data?.items ?? [];
  // Apply the optimistic local order, but only over rows the server
  // actually returned — any drop that targeted a now-deleted row
  // falls through to the server order.
  const items = localOrder
    ? (() => {
        const byId = new Map(serverItems.map((c) => [c.id, c]));
        const ordered: BoardColumn[] = [];
        for (const id of localOrder) {
          const c = byId.get(id);
          if (c) ordered.push(c);
        }
        for (const c of serverItems) {
          if (!localOrder.includes(c.id)) ordered.push(c);
        }
        return ordered;
      })()
    : serverItems;

  const onDropReorder = (newIds: string[]) => {
    setLocalOrder(newIds);
    reorder.mutate(newIds);
  };

  if (list.isLoading) return <Spinner />;
  if (list.error) {
    return (
      <div className="alert alert--danger">
        Failed to load board columns: {(list.error as Error).message}
      </div>
    );
  }

  return (
    <>
      <PageHeader
        title="Triage board columns"
        actions={
          <Link to={routes.BOARD_COLUMNS_NEW} className="btn btn--primary btn--sm">
            New column
          </Link>
        }
      />
      {items.length === 0 ? (
        <div style={emptyStateStyle}>
          <h3 style={{ marginTop: 0 }}>No board columns yet</h3>
          <p className="t-secondary">
            Create columns to define your triage outcomes. Workflow authors will attach them to
            edges.
          </p>
          <Link to={routes.BOARD_COLUMNS_NEW} className="btn btn--primary btn--sm">
            Create your first column
          </Link>
        </div>
      ) : (
        <ReorderableList
          items={items}
          onChangeOrder={onDropReorder}
          isReordering={reorder.isPending}
          onRowOpen={async (id) => {
            try {
              await nav({ to: routes.boardColumnDetail(id) });
            } catch (err) {
              console.error(err);
            }
          }}
        />
      )}
      {reorder.error ? (
        <div className="alert alert--danger" style={{ marginTop: 'var(--sp-2)' }}>
          Failed to save new order: {(reorder.error as Error).message}
        </div>
      ) : null}
    </>
  );
}

function ReorderableList({
  items,
  onChangeOrder,
  isReordering,
  onRowOpen,
}: {
  items: readonly BoardColumn[];
  onChangeOrder: (newIds: string[]) => void;
  isReordering: boolean;
  onRowOpen: (id: string) => void;
}) {
  // `draggedId` is the row picked up; `dragOverId` is whatever row the
  // cursor is currently over so we can render an insertion line above
  // it. Both clear on dragend / drop.
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const handleDragStart = (e: DragEvent<HTMLDivElement>, id: string) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = 'move';
    // Required for Firefox to actually start the drag.
    e.dataTransfer.setData('text/plain', id);
  };
  const handleDragOver = (e: DragEvent<HTMLDivElement>, id: string) => {
    if (!draggedId || draggedId === id) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverId !== id) setDragOverId(id);
  };
  const handleDragLeave = (e: DragEvent<HTMLDivElement>, id: string) => {
    // Only clear if leaving the row that was being highlighted —
    // dragleave fires for every child as the cursor moves across them.
    if (dragOverId === id && !e.currentTarget.contains(e.relatedTarget as Node | null)) {
      setDragOverId(null);
    }
  };
  const handleDrop = (e: DragEvent<HTMLDivElement>, targetId: string) => {
    e.preventDefault();
    setDragOverId(null);
    if (!draggedId || draggedId === targetId) {
      setDraggedId(null);
      return;
    }
    const ids = items.map((c) => c.id);
    const fromIdx = ids.indexOf(draggedId);
    const toIdx = ids.indexOf(targetId);
    if (fromIdx === -1 || toIdx === -1) {
      setDraggedId(null);
      return;
    }
    const next = [...ids];
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, draggedId);
    setDraggedId(null);
    onChangeOrder(next);
  };
  const handleDragEnd = () => {
    setDraggedId(null);
    setDragOverId(null);
  };

  return (
    <div style={listStyle} role="list" aria-label="Reorderable triage board columns">
      {items.map((c) => {
        const isDragged = draggedId === c.id;
        const isDropTarget = dragOverId === c.id && draggedId !== null && draggedId !== c.id;
        return (
          <div
            key={c.id}
            role="listitem"
            draggable={!isReordering}
            onDragStart={(e) => handleDragStart(e, c.id)}
            onDragOver={(e) => handleDragOver(e, c.id)}
            onDragLeave={(e) => handleDragLeave(e, c.id)}
            onDrop={(e) => handleDrop(e, c.id)}
            onDragEnd={handleDragEnd}
            onClick={() => onRowOpen(c.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') onRowOpen(c.id);
            }}
            tabIndex={0}
            aria-grabbed={isDragged}
            style={rowStyle(isDragged, isDropTarget, isReordering)}
          >
            <span
              aria-hidden="true"
              style={dragHandleStyle}
              title="Drag to reorder"
              // Stop click-through so clicking the handle itself doesn't
              // open the detail page.
              onClick={(e) => e.stopPropagation()}
            >
              ⋮⋮
            </span>
            {c.color ? (
              <span
                aria-hidden="true"
                style={{
                  ...swatchStyle,
                  background: boardColumnAccentToken[c.color] ?? 'var(--surface-3)',
                }}
              />
            ) : (
              <span aria-hidden="true" style={{ ...swatchStyle, background: 'transparent' }} />
            )}
            <Link
              to={routes.boardColumnDetail(c.id)}
              style={nameLinkStyle}
              onClick={(e) => e.stopPropagation()}
            >
              {c.name}
            </Link>
            <span style={descriptionStyle}>
              {c.description ?? <span className="t-muted">—</span>}
            </span>
            <span className="t-xs t-secondary" style={updatedStyle}>
              {formatRelative(c.updatedAt)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const listStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
  overflow: 'hidden',
  background: 'var(--surface-2)',
};

const rowStyle = (
  isDragged: boolean,
  isDropTarget: boolean,
  isReordering: boolean,
): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: '24px 16px minmax(160px, 1fr) minmax(0, 2fr) 100px',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  padding: '8px 12px',
  background: isDropTarget
    ? 'var(--surface-3, color-mix(in srgb, var(--horizon-glow) 8%, var(--surface)))'
    : 'var(--surface)',
  cursor: isReordering ? 'wait' : 'grab',
  opacity: isDragged ? 0.4 : 1,
  borderTop: isDropTarget ? '2px solid var(--horizon-glow)' : '2px solid transparent',
  transition: 'background 100ms ease, opacity 100ms ease, border-color 100ms ease',
});

const dragHandleStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  color: 'var(--text-muted)',
  fontSize: 14,
  cursor: 'grab',
  userSelect: 'none',
  letterSpacing: -2,
};

const swatchStyle: CSSProperties = {
  display: 'inline-block',
  width: 12,
  height: 12,
  borderRadius: 3,
  border: '1px solid var(--border-subtle)',
};

const nameLinkStyle: CSSProperties = {
  color: 'var(--text-primary)',
  fontWeight: 'var(--fw-med)',
  fontSize: 'var(--fs-sm)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const descriptionStyle: CSSProperties = {
  color: 'var(--text-secondary)',
  fontSize: 'var(--fs-sm)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const updatedStyle: CSSProperties = {
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

const emptyStateStyle: CSSProperties = {
  padding: 'var(--sp-4)',
  textAlign: 'center',
  background: 'var(--surface-2)',
  border: '1px solid var(--border-subtle)',
  borderRadius: 'var(--radius-sm)',
};
